// Implementation derived from https://github.com/dohomi/react-hook-form-mui
//
// MIT License

// Copyright (c) 2020 Dominic Garms

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { TextField, TextFieldProps } from '@mui/material';
import { forwardRef } from 'react';

import {
  Control,
  Controller,
  ControllerProps,
  FieldError,
} from 'react-hook-form';

export type TextFieldElementProps = Omit<TextFieldProps, 'name'> & {
  validation?: ControllerProps['rules'];
  name: string;
  parseError?: (error: FieldError) => string;
  control?: Control<any>;
};

const HooksTextField = forwardRef(
  (props: TextFieldElementProps, refs): JSX.Element => {
    const {
      validation = {},
      parseError,
      type,
      required,
      name,
      control,
      ...rest
    } = props;
    if (required) {
      validation.required = '必須項目です。';
    }
    if (type === 'email') {
      validation.pattern = {
        // eslint-disable-next-line no-useless-escape
        value:
          /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        message: '有効なメールアドレスではありません。',
      };
    }
    return (
      <Controller
        name={name}
        control={control}
        rules={validation}
        render={({
          field: { value, onChange, onBlur },
          fieldState: { invalid, error },
        }) => (
          <TextField
            {...rest}
            name={name}
            value={value ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            type={type}
            error={invalid}
            helperText={
              error
                ? typeof parseError === 'function'
                  ? parseError(error)
                  : error.message
                : rest.helperText
            }
          />
        )}
      />
    );
  }
);

export default HooksTextField;
